require("./ynet_site_articleTimelineNewComponenta.less")
import { HyperLinkBuilder } from "widgets/commonComponents/inline/hyperLink"
import { BaseLinkTypes } from "global/components/linkSettings/interfaces"
import {
    DEFAULT_TIMELINE_CTA_LABEL,
    TimelineData,
    TimelineItemData,
    TimelineSegmentData,
} from "articlePage/timelineArticle/timelineSpecialData"
import { TimelineFrontViewModel } from "./timelineFrontData"
import { TimelineItemMedia } from "./timelineItemMedia"
import { TimelineItemText } from "./timelineItemText"

export interface SiteArticleTimelineNewProps extends TimelineFrontViewModel {
    isMobileWeb?: boolean
    isApp?: boolean
}

interface SiteArticleTimelineNewState {
    expandedItemIds: { [itemId: string]: boolean }
    segmentsExpanded: boolean
    activeItemKey: string
}

interface OrderedTimelineItem {
    key: string
    segment: TimelineSegmentData
    item: TimelineItemData
    isFirstVisible: boolean
    showSegmentTitle: boolean
}

export class SiteArticleTimelineNew extends React.Component<SiteArticleTimelineNewProps, SiteArticleTimelineNewState> {
    public static siteScriptName = "SiteArticleTimelineNew"

    private itemRefs: { [key: string]: HTMLDivElement } = {}
    private observer: IntersectionObserver = null

    constructor(props: SiteArticleTimelineNewProps) {
        super(props)
        this.state = {
            expandedItemIds: {},
            segmentsExpanded: false,
            activeItemKey: "",
        }
    }

    public componentDidMount() {
        this.setupProgressiveObserver()
    }

    public componentWillUnmount() {
        this.teardownProgressiveObserver()
    }

    private getTimelineData = (): TimelineData => this.props.timelineData

    private getOrderedItems = (timelineData: TimelineData): OrderedTimelineItem[] => {
        const reverse = timelineData.displayOrder === "last_to_first"
        let segments = (timelineData.segments || []).slice()
        if (reverse) {
            segments = segments.reverse()
        }

        const limitActive = timelineData.displayMode === "full"
            && timelineData.limitSegments
            && !this.state.segmentsExpanded
        const visibleSegments = limitActive
            ? segments.slice(0, Math.max(1, timelineData.maxSegments || 1))
            : segments

        const ordered: OrderedTimelineItem[] = []
        visibleSegments.forEach(segment => {
            let items = (segment.items || []).slice()
            if (reverse) {
                items = items.reverse()
            }

            items.forEach((item, itemIndex) => {
                ordered.push({
                    key: `${segment.id}_${item.id}`,
                    segment,
                    item,
                    isFirstVisible: ordered.length === 0,
                    showSegmentTitle: !!timelineData.showSegmentTitles && itemIndex === 0 && !!segment.title,
                })
            })

            if (!items.length && timelineData.showSegmentTitles && segment.title) {
                ordered.push({
                    key: `${segment.id}_empty`,
                    segment,
                    item: null,
                    isFirstVisible: ordered.length === 0,
                    showSegmentTitle: true,
                })
            }
        })

        return ordered
    }

    private setupProgressiveObserver = () => {
        const timelineData = this.getTimelineData()
        if (!timelineData || timelineData.displayMode !== "progressive" || typeof IntersectionObserver === "undefined") {
            return
        }

        this.observer = new IntersectionObserver(this.onItemsIntersect, {
            root: null,
            rootMargin: "-40% 0px -40% 0px",
            threshold: [0, 0.25, 0.5, 0.75, 1],
        })

        Object.keys(this.itemRefs).forEach(key => {
            const el = this.itemRefs[key]
            if (el) {
                this.observer.observe(el)
            }
        })
    }

    private teardownProgressiveObserver = () => {
        if (this.observer) {
            this.observer.disconnect()
            this.observer = null
        }
    }

    private onItemsIntersect = (entries: IntersectionObserverEntry[]) => {
        let bestKey = this.state.activeItemKey
        let bestRatio = 0

        entries.forEach(entry => {
            const key = (entry.target as HTMLElement).getAttribute("data-timeline-key") || ""
            if (entry.isIntersecting && entry.intersectionRatio >= bestRatio) {
                bestRatio = entry.intersectionRatio
                bestKey = key
            }
        })

        if (bestKey && bestKey !== this.state.activeItemKey) {
            this.setState({ activeItemKey: bestKey })
        }
    }

    private setItemRef = (key: string, el: HTMLDivElement) => {
        if (el) {
            this.itemRefs[key] = el
            if (this.observer) {
                this.observer.observe(el)
            }
        } else if (this.itemRefs[key]) {
            if (this.observer) {
                this.observer.unobserve(this.itemRefs[key])
            }
            delete this.itemRefs[key]
        }
    }

    private expandItem = (itemId: string) => {
        this.setState(prev => ({
            expandedItemIds: { ...prev.expandedItemIds, [itemId]: true },
        }))
    }

    private expandSegments = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        this.setState({ segmentsExpanded: true })
    }

    private getProgressiveOpacityClass = (key: string, index: number, ordered: OrderedTimelineItem[]) => {
        const timelineData = this.getTimelineData()
        if (!timelineData || timelineData.displayMode !== "progressive") {
            return "opacity-full"
        }

        const activeKey = this.state.activeItemKey || (ordered[0] && ordered[0].key)
        if (!activeKey) {
            return index === 0 ? "opacity-full" : "opacity-hidden"
        }

        const activeIndex = ordered.findIndex(entry => entry.key === activeKey)
        if (index === activeIndex) {
            return "opacity-full"
        }
        if (index < activeIndex) {
            return "opacity-dimmed"
        }
        return "opacity-hidden"
    }

    private renderHeader = () => {
        const { roof, title, subTitle } = this.props

        return (
            <div className="timeline-new-header">
                {roof && <div className="timeline-new-roof">{roof}</div>}
                {title && <h1 className="timeline-new-title">{title}</h1>}
                {subTitle && <div className="timeline-new-subtitle">{subTitle}</div>}
            </div>
        )
    }

    private renderSegmentTitle = (segmentTitle: string) => (
        <div className="timeline-new-segment-title">
            <div className="timeline-new-segment-title-row">
                <div className="timeline-new-segment-title-text">{segmentTitle}</div>
                <div className="timeline-new-segment-title-line" />
            </div>
            <div className="timeline-new-segment-bullet" />
        </div>
    )

    private renderCta = (item: TimelineItemData) => {
        if (!item.ctaEnabled || !item.ctaLinkProperties || item.ctaLinkProperties.type === BaseLinkTypes.NONE) {
            return null
        }

        const label = item.ctaLabel || DEFAULT_TIMELINE_CTA_LABEL
        const href = (this.props.ctaHrefs && this.props.ctaHrefs[item.id]) || ""
        if (!href) {
            return null
        }

        return (
            <div className="timeline-new-item-cta">
                <HyperLinkBuilder
                    href={href}
                    className="timeline-new-item-cta-link"
                    link={item.ctaLinkProperties}
                    linkTarget={item.ctaLinkTargetProperties}
                >
                    <span>{label}</span>

                    <span className="timeline-new-item-cta-icon" aria-hidden="true" />
                </HyperLinkBuilder>
            </div>
        )
    }

    private renderItem = (entry: OrderedTimelineItem, index: number, ordered: OrderedTimelineItem[], mediaLabel: string) => {
        const { images, videos, isMobileWeb, isApp } = this.props
        const { item } = entry
        if (!item) {
            return (
                <div key={entry.key} className="timeline-new-entry">
                    {entry.showSegmentTitle && this.renderSegmentTitle(entry.segment.title)}
                </div>
            )
        }

        const mediaAbove = item.mediaPosition !== "below"
        const opacityClass = this.getProgressiveOpacityClass(entry.key, index, ordered)
        const showMediaLabel = entry.isFirstVisible && !!mediaLabel

        return (
            <div
                key={entry.key}
                data-timeline-key={entry.key}
                ref={el => this.setItemRef(entry.key, el)}
                className={`timeline-new-entry ${opacityClass}`}
            >
                {entry.showSegmentTitle && this.renderSegmentTitle(entry.segment.title)}

                <div className={`timeline-new-item ${mediaAbove ? "" : "is-media-below"}`}>
                    <div className="timeline-new-item-content">
                        {showMediaLabel &&
                            <div className="timeline-new-media-label">{mediaLabel}</div>
                        }

                        {item.roof && <div className="timeline-new-item-roof">{item.roof}</div>}

                        {item.title && <h3 className="timeline-new-item-title">{item.title}</h3>}

                        {mediaAbove &&
                            <TimelineItemMedia
                                item={item}
                                articleId={this.props.articleId}
                                images={images}
                                videos={videos}
                                isMobileWeb={isMobileWeb}
                                isApp={isApp}
                            />
                        }

                        <TimelineItemText
                            text={item.text || ""}
                            isExpanded={!!this.state.expandedItemIds[item.id]}
                            onExpand={() => this.expandItem(item.id)}
                        />

                        {!mediaAbove &&
                            <TimelineItemMedia
                                item={item}
                                articleId={this.props.articleId}
                                images={images}
                                videos={videos}
                                isMobileWeb={isMobileWeb}
                                isApp={isApp}
                            />
                        }

                        {this.renderCta(item)}
                    </div>

                    <div className="timeline-new-item-node" />
                </div>
            </div>
        )
    }

    public render() {
        const { isMobileWeb, isApp, timelineData } = this.props
        if (!timelineData) {
            return null
        }

        const ordered = this.getOrderedItems(timelineData)
        const hasMoreSegments = timelineData.displayMode === "full"
            && timelineData.limitSegments
            && !this.state.segmentsExpanded
            && (timelineData.segments || []).length > (timelineData.maxSegments || 1)

        const mediaLabel = timelineData.displayOrder === "last_to_first"
            ? (timelineData.mediaLabel || "")
            : ""

        const activeKey = this.state.activeItemKey || (ordered[0] && ordered[0].key)
        const activeIndex = Math.max(0, ordered.findIndex(entry => entry.key === activeKey))
        const progressRatio = ordered.length > 1 ? activeIndex / (ordered.length - 1) : 1

        return (
            <div className={`article-timeline-new-front ${isMobileWeb ? "is-mobile" : ""} ${isApp ? "is-app" : ""} ${timelineData.displayMode === "progressive" ? "is-progressive" : ""}`}>
                {this.renderHeader()}

                <div className="timeline-new-structure">
                    <div className="timeline-new-spine" />
                    {timelineData.displayMode === "progressive" &&
                        <div className="timeline-new-spine-progress" style={{ height: `${Math.round(progressRatio * 100)}%` }} />
                    }

                    {ordered.map((entry, index) => this.renderItem(entry, index, ordered, mediaLabel))}

                    {!hasMoreSegments && <div className="timeline-new-end-node" aria-hidden="true" />}

                    {hasMoreSegments &&
                        <div className="timeline-new-segments-more">
                            <div className="timeline-new-segments-fade" aria-hidden="true" />
                            <button type="button" className="timeline-new-show-more-btn" onClick={this.expandSegments}>
                                <span>{__("Show more")}</span>
                                <span className="timeline-new-show-more-icon" aria-hidden="true" />
                            </button>
                        </div>
                    }
                </div>
            </div>
        )
    }
}
